/**
 * Product Tabs Component
 * Manages tab switching and recently viewed products loading
 */
class ProductTabsComponent extends HTMLElement {
  connectedCallback() {
    // Load translatable texts from data attributes
    this.texts = {
      viewOptions: this.dataset.textViewOptions || 'VER OPCIONES',
      addToCart: this.dataset.textAddToCart || 'Añadir',
      added: this.dataset.textAdded || 'Agregada',
      bajopedido: this.dataset.textBajopedido || 'AÑADIR BAJO PEDIDO',
      salePrice: this.dataset.textSalePrice || 'Precio de oferta',
      regularPrice: this.dataset.textRegularPrice || 'Precio habitual',
      save: this.dataset.textSave || 'ahorra',
      noRecentlyViewed: this.dataset.textNoRecentlyViewed || 'No has visto productos recientemente'
    };
    
    this.tabs = this.querySelectorAll('.product-tabs__tab');
    this.panels = this.querySelectorAll('.product-tabs__panel');
    this.recentlyViewedContainer = this.querySelector('[data-recently-viewed]');
    
    // Setup tab switching
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (e) => this.handleTabClick(e));
    });
    
    // Load recently viewed products if tab 2 exists
    if (this.recentlyViewedContainer) {
      // Observe when tab 2 becomes visible
      this.setupRecentlyViewedObserver();
    }
  }
  
  /**
   * Handle tab click event
   * @param {Event} e - Click event
   */
  handleTabClick(e) {
    const clickedTab = e.currentTarget;
    const tabIndex = clickedTab.dataset.tabIndex;
    
    // Update tabs
    this.tabs.forEach(tab => {
      tab.setAttribute('aria-selected', 'false');
    });
    clickedTab.setAttribute('aria-selected', 'true');
    
    // Update panels
    this.panels.forEach(panel => {
      panel.hidden = true;
    });
    
    const targetPanel = this.querySelector(`[data-tab-panel="${tabIndex}"]`);
    
    if (targetPanel) {
      targetPanel.hidden = false;
      
      // If switching to recently viewed tab, load products
      if (tabIndex === '1' && this.recentlyViewedContainer && !this.recentlyViewedLoaded) {
        this.loadRecentlyViewed();
      }
    }
  }
  
  /**
   * Setup IntersectionObserver for lazy loading recently viewed
   */
  setupRecentlyViewedObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.recentlyViewedLoaded) {
            this.loadRecentlyViewed();
            observer.disconnect();
          }
        });
      },
      { rootMargin: '100px' }
    );
    
    const tab2Panel = this.querySelector('[data-tab-panel="1"]');
    if (tab2Panel) {
      observer.observe(tab2Panel);
    }
  }
  
  /**
   * Load recently viewed products
   */
  async loadRecentlyViewed() {
    if (this.recentlyViewedLoaded) return;
    
    try{
      // Get recently viewed products from localStorage (with full data)
      const products = this.getRecentlyViewedIds();
      
      if (products.length === 0) {
        this.showEmptyState();
        return;
      }
      
      // Update existing product cards with data from localStorage
      this.updateProductCards(products);
      
      this.recentlyViewedLoaded = true;
      
    } catch (error) {
      this.showEmptyState();
    }
  }
  
  /**
   * Get recently viewed products from localStorage (with full data)
   * @returns {Array<Object>} Array of product objects with all data
   */
  getRecentlyViewedIds() {
    try {
      // IMPORTANTE: Usar 'recentlyViewedProducts' - sistema que guarda datos completos
      const stored = localStorage.getItem('recentlyViewedProducts');
      
      if (!stored) {
        return [];
      }
      
      const products = JSON.parse(stored);
      
      return Array.isArray(products) ? products : [];
    } catch (error) {
      return [];
    }
  }
  
  /**
   * Fetch product data from Shopify API
   * @param {Array<string>} productHandles - Array of product handles
   * @returns {Promise<Array>} Array of product objects
   */
  async fetchProductsData(productHandles) {
    const promises = productHandles.map(async (handle) => {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        return null;
      }
    });
    
    const results = await Promise.all(promises);
    const products = results.filter(p => p !== null);
    
    return products;
  }
  
  /**
   * Update existing product cards with new product data
   * @param {Array} products - Array of product objects
   */
  updateProductCards(products) {
    const productItems = this.recentlyViewedContainer.querySelectorAll('[data-recently-viewed-item]');
    
    // Update or hide each item
    productItems.forEach((item, index) => {
      if (index >= products.length) {
        // Hide extra placeholders
        item.style.display = 'none';
        item.classList.add('hidden');
      } else {
        // Update with real product data
        const product = products[index];
        item.style.display = '';
        item.classList.remove('hidden');
        this.updateSingleCard(item, product);
      }
    });
    
  }
  
  /**
   * Update a single product card with new data
   * @param {HTMLElement} cardItem - The card container element
   * @param {Object} product - Product data from Shopify API
   */
  updateSingleCard(cardItem, product) {
    this.updateCardBasicInfo(cardItem, product);
  }
  
  /**
   * Fallback method to update basic card info when full replacement fails
   * @param {HTMLElement} cardItem - The card container element
   * @param {Object} product - Product data from Shopify API
   */
  updateCardBasicInfo(cardItem, product) {
    // Validate product has variants
    if (!product.variants || !Array.isArray(product.variants) || product.variants.length === 0) {
      return;
    }
    
    const variant = product.variants[0];
    // Handle featured_image - can be null, empty string, or valid URL
    const image = (product.featured_image && product.featured_image.trim() !== '') 
      ? product.featured_image 
      : (product.images?.[0] || null);
    
    // 1. Update product-card data-product-id
    const productCard = cardItem.querySelector('product-card');
    if (productCard) {
      productCard.dataset.productId = product.id;
    }
    
    // 2. Update ALL links to product URL
    const links = cardItem.querySelectorAll('a[href]');
    links.forEach((link) => {
      // Skip visually-hidden links but update all others
      if (!link.classList.contains('visually-hidden')) {
        link.href = `${product.url}?variant=${variant.id}`;
      }
    });
    
    // 3. Update or create placeholder for media block
    const mediaBlock = cardItem.querySelector('[ref="cardGallery"], .card-gallery');
    
    // Update mediaBlock data-product-id to match current product
    if (mediaBlock) {
      mediaBlock.dataset.productId = product.id;
    }
    
    if (image) {
      // Product has image - update or create slideshow
      if (mediaBlock) {
        let slideshow = mediaBlock.querySelector('slideshow-component');
        
        if (slideshow) {
          // Slideshow exists - update images
          const images = slideshow.querySelectorAll('slideshow-slide img');
          images.forEach((img) => {
            img.src = image;
            img.srcset = '';
            img.alt = product.title;
          });
          
          // Remove placeholder if exists
          const placeholder = mediaBlock.querySelector('.product-card-placeholder, product-title.product-card-gallery__title-placeholder');
          if (placeholder) {
            placeholder.remove();
          }
          
          slideshow.style.display = '';
          
        } else {
          // No slideshow - create complete structure including link wrapper
          const placeholder = mediaBlock.querySelector('.product-card-placeholder, product-title.product-card-gallery__title-placeholder');
          if (placeholder) {
            placeholder.remove();
          }
          
          // Build complete structure: <a> wrapper + slideshow
          const completeHTML = `
<a class="contents" ref="cardGalleryLink" href="${product.url}?variant=${variant.id}" aria-label="${product.title}">
  <slideshow-component ref="slideshow" style="--slideshow-timeline: --slide-1;" initial-slide="0" infinite="" data-generic-media-size="1">
    <slideshow-container ref="slideshowContainer">
      <slideshow-slides tabindex="-1" ref="scroller">
        <slideshow-slide ref="slides[]" aria-hidden="false" style="view-timeline-name: --slide-1; --product-media-fit: cover;" class="product-media-container media-fit product-media-container--image" slide-id="${product.id}">
          <div class="product-media" style="--ratio: 1.0" data-media-id="${product.id}">
            <img src="${image}" alt="${product.title}" srcset="" width="3840" height="3840" loading="lazy" sizes="auto" class="product-media__image" transitiontoproduct="false" data_max_resolution="${image}" fetchpriority="auto" style="--focal-point: 50.0% 50.0%;">
          </div>
        </slideshow-slide>
      </slideshow-slides>
    </slideshow-container>
  </slideshow-component>
</a>
          `.trim();
          
          // Insert before product-badges (if exists) or as first child
          const badges = mediaBlock.querySelector('.product-badges');
          if (badges) {
            badges.insertAdjacentHTML('beforebegin', completeHTML);
          } else {
            mediaBlock.insertAdjacentHTML('afterbegin', completeHTML);
          }
        }
      }
    } else {
      // Product has NO image - show placeholder with product title
      if (mediaBlock) {
        // Hide slideshow
        const slideshow = mediaBlock.querySelector('slideshow-component');
        if (slideshow) {
          slideshow.style.display = 'none';
        }
        
        // Create or update placeholder
        let placeholder = mediaBlock.querySelector('.product-card-placeholder');
        if (!placeholder) {
          placeholder = document.createElement('div');
          placeholder.className = 'product-card-placeholder';
          placeholder.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            aspect-ratio: var(--gallery-aspect-ratio, 4/5);
            background: #f5f5f5;
            padding: 20px;
            text-align: center;
          `;
          mediaBlock.appendChild(placeholder);
        }
        
        placeholder.innerHTML = `
          <p style="
            font-size: 14px;
            color: #666;
            line-height: 1.4;
            margin: 0;
            word-break: break-word;
          ">${product.title}</p>
        `;
      }
    }
    
    // 4. Update H3/H4/P title (product-grid-view-zoom-out--details)
    const zoomOutDetails = cardItem.querySelector('.product-grid-view-zoom-out--details');
    if (zoomOutDetails) {
      const titleEl = zoomOutDetails.querySelector('h3, h4, p.h4, .h4');
      if (titleEl) {
        titleEl.textContent = product.title;
      }
    }
    
    // 5. Update vendor badge (first text-block inside group-block-content, has background style)
    const vendorBadge = cardItem.querySelector('.group-block-content > .text-block--background');
    if (vendorBadge) {
      const vendorText = vendorBadge.querySelector('p');
      if (product.vendor) {
        vendorBadge.style.visibility = '';
        if (vendorText) vendorText.textContent = product.vendor;
      } else {
        // Keep space reserved but hide text if no vendor data
        vendorBadge.style.visibility = 'hidden';
      }
    }
    
    // 6. Update product title link (text-block inside productTitleLink)
    const titleLink = cardItem.querySelector('[ref="productTitleLink"] .text-block p');
    if (titleLink) {
      titleLink.textContent = product.title;
    }
    
    // 7. Update visually-hidden title
    const hiddenTitle = cardItem.querySelector('.product-card__link .visually-hidden');
    if (hiddenTitle) {
      hiddenTitle.textContent = product.title;
    }
    
    // 8. Update prices in product-grid-view-zoom-out--details (H6 price)
    const zoomOutPrice = cardItem.querySelector('.product-grid-view-zoom-out--details .h6 product-price');
    if (zoomOutPrice) {
      zoomOutPrice.dataset.productId = product.id;
      this.updateSimplePrice(zoomOutPrice, variant);
    }
    
    // 9. Update prices in product-card-price (detailed price with compact layout)
    const cardPrice = cardItem.querySelector('.product-card-price');
    if (cardPrice) {
      cardPrice.dataset.productId = product.id;
      this.updateCompactPrice(cardPrice, variant);
    }
    
    // 10. Update product badges (Oferta badge)
    const productBadges = cardItem.querySelector('.product-badges');
    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (productBadges) {
      const ofertaBadge = productBadges.querySelector('.product-badges__badge');
      if (hasDiscount && ofertaBadge) {
        ofertaBadge.style.display = '';
        productBadges.style.display = '';
      } else if (productBadges) {
        productBadges.style.display = 'none';
      }
    }
    
    // 10. Update product-card data-product-variants-size
    if (productCard) {
      productCard.dataset.productVariantsSize = product.variants_count || product.variants?.length || 1;
    }
    
    // 11. Rebuild button in product-card-button-wrapper
    // CRITICAL: Completely rebuild the button HTML based on has_combined_products
    const buttonWrapper = cardItem.querySelector('.product-card-button-wrapper');
    
    if (buttonWrapper) {
      const hasCombinedProducts = product.has_combined_products === true;
      const isBajopedido = product.is_bajopedido === true;
      
      // Bajo pedido products always get quick-add (even with combined products)
      // because they should add to cart directly, not navigate to product page
      const shouldShowLink = hasCombinedProducts && !isBajopedido;
      
      // Clear all existing content
      const existingQuickAdd = buttonWrapper.querySelector('quick-add-component');
      const existingButton = buttonWrapper.querySelector('a.product-card-button-link, a.button, a.button-secondary, a.custom-button, a[class*="custom-button--"]');
      
      if (shouldShowLink) {
        // Product HAS combined products → Show "VER OPCIONES" button
        
        // Remove quick-add if exists
        if (existingQuickAdd) {
          existingQuickAdd.remove();
        }
        
        // Create or update button
        if (existingButton) {
          existingButton.href = `${product.url}?variant=${variant.id}`;
          const buttonLabel = existingButton.querySelector('.button-label');
          if (buttonLabel) {
            buttonLabel.textContent = this.texts.viewOptions;
          }
        } else {
          // Create new button - find a reference button from another card to copy classes
          const referenceButton = document.querySelector('.product-card-button-link, a.button.product-card-button-link');
          const newButton = document.createElement('a');
          newButton.href = `${product.url}?variant=${variant.id}`;
          
          // Copy classes from reference button if available, otherwise use defaults
          if (referenceButton) {
            newButton.className = referenceButton.className;
            // Copy inline styles if any
            if (referenceButton.getAttribute('style')) {
              newButton.setAttribute('style', referenceButton.getAttribute('style'));
            }
          } else {
            newButton.className = 'size-style product-card-button-link button';
          }
          
          newButton.innerHTML = `<span class="button-label">${this.texts.viewOptions}</span>`;
          buttonWrapper.appendChild(newButton);
        }
        
      } else if (isBajopedido) {
        // Bajo pedido product → Show direct add-to-cart (bypasses product-form-component)
        const buttonText = this.texts.bajopedido;

        // Remove any existing content
        if (existingButton) existingButton.remove();
        if (existingQuickAdd) existingQuickAdd.remove();
        const existingDirect = buttonWrapper.querySelector('.bajopedido-direct-add');
        if (existingDirect) existingDirect.remove();

        const directAddHTML = `
<div class="bajopedido-direct-add"
  data-bajopedido-variant-id="${variant.id}"
  data-bajopedido-product-id="${product.id}"
>
  <button type="button" class="button quick-add__button bajopedido-direct-add__button" data-bajopedido-trigger aria-label="${buttonText} - ${product.title}">
    <span class="add-to-cart-text">
      <span class="svg-wrapper add-to-cart-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"></path>
          <path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width)" d="M14.608 12.563v5m2.5-2.5h-5"></path>
        </svg>
      </span>
      <span class="add-to-cart-text__content">${buttonText}</span>
    </span>
    <span aria-hidden="true" class="add-to-cart-text--added">
      <span class="svg-wrapper add-to-cart-icon--added">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-checkmark">
          <path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      </span>
      <span>${this.texts.added}</span>
    </span>
  </button>
</div>
        `.trim();

        buttonWrapper.innerHTML = directAddHTML;

      } else {
        // Regular product (no combined, no bajo pedido) → Show quick-add
        const buttonText = this.texts.addToCart;
        
        // Remove button if exists
        if (existingButton) {
          existingButton.remove();
        }
        // Remove bajo pedido direct add if exists
        const existingDirect = buttonWrapper.querySelector('.bajopedido-direct-add');
        if (existingDirect) existingDirect.remove();
        
        // Update quick-add if exists, otherwise create it
        if (existingQuickAdd) {
          const productForm = existingQuickAdd.querySelector('product-form-component');
          if (productForm) {
            existingQuickAdd.dataset.productTitle = product.title;
            productForm.dataset.productId = product.id;
            
            const variantInput = productForm.querySelector('input[name="id"]');
            if (variantInput) {
              variantInput.value = variant.id;
            }
            
            // Update button text
            const textContent = productForm.querySelector('.add-to-cart-text__content');
            if (textContent) {
              textContent.textContent = buttonText;
            }
            
            // Update classes based on variant count
            const variantCount = product.variants_count || product.variants?.length || 1;
            productForm.classList.remove('quick-add__product-form-component--single-option', 'quick-add__product-form-component--multi-variant', 'quick-add__product-form-component--single-variant');
            
            if (variantCount === 1) {
              productForm.classList.add('quick-add__product-form-component--single-option', 'quick-add__product-form-component--single-variant');
            } else {
              productForm.classList.add('quick-add__product-form-component--multi-variant');
            }
          }
        } else {
          // No quick-add in placeholder → Create complete quick-add HTML
          const variantCount = product.variants_count || product.variants?.length || 1;
          const sectionId = buttonWrapper.closest('[data-section-id]')?.dataset.sectionId || 'recently-viewed';
          const formId = `QuickAdd-ProductForm-${product.id}-recently-viewed`;
          
          const quickAddHTML = `
<quick-add-component class="quick-add color-scheme-1" ref="quickAdd" data-product-title="${product.title}">
  <product-form-component 
    data-section-id="${sectionId}" 
    data-product-id="${product.id}" 
    on:submit="/handleSubmit" 
    class="quick-add__product-form-component ${variantCount === 1 ? 'quick-add__product-form-component--single-option quick-add__product-form-component--single-variant' : 'quick-add__product-form-component--multi-variant'}"
  >
    <div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>
    <form method="post" action="/cart/add" id="${formId}" accept-charset="UTF-8" class="shopify-product-form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
      <input type="hidden" name="form_type" value="product">
      <input type="hidden" name="utf8" value="✓">
      <input type="hidden" name="id" ref="variantId" value="${variant.id}">
      <input type="hidden" name="quantity" value="1">
      <add-to-cart-component ref="addToCartButtonContainer" data-product-variant-media="" data-add-to-cart-animation="true">
        <button type="submit" name="add" ref="addToCartButton" on:click="/handleClick" class="button button quick-add__button quick-add__button--add">
          <span class="add-to-cart-text">
            <span class="svg-wrapper add-to-cart-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"></path>
                <path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width)" d="M14.608 12.563v5m2.5-2.5h-5"></path>
              </svg>
            </span>
            <span class="add-to-cart-text__content is-visually-hidden-mobile">${buttonText}</span>
          </span>
          <span aria-hidden="true" class="add-to-cart-text--added">
            <span class="svg-wrapper add-to-cart-icon--added">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-checkmark">
                <path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </span>
            <span class="is-visually-hidden-mobile">${this.texts.added}</span>
          </span>
        </button>
      </add-to-cart-component>
      <input type="hidden" name="product-id" value="${product.id}">
      <input type="hidden" name="section-id" value="${sectionId}">
    </form>
  </product-form-component>
</quick-add-component>
          `.trim();
          
          buttonWrapper.innerHTML = quickAddHTML;
        }
      }
    }
  }
  
  /**
   * Update simple price (H6 in zoom-out details)
   * Structure: <span class="compare-at-price">60,62 €</span> <span class="price">46,67 €</span>
   * @param {HTMLElement} priceContainer - The product-price element
   * @param {Object} variant - Variant data
   */
  updateSimplePrice(priceContainer, variant) {
    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
    
    // Find the price wrapper div inside product-price
    const priceWrapper = priceContainer.querySelector('[ref="priceContainer"]');
    if (!priceWrapper) {
      return;
    }
    
    // Clear existing content
    priceWrapper.innerHTML = '';
    
    // Add compare-at-price if there's a discount
    if (hasDiscount) {
      const compareSpan = document.createElement('span');
      compareSpan.className = 'compare-at-price';
      compareSpan.textContent = this.formatMoney(variant.compare_at_price);
      priceWrapper.appendChild(compareSpan);
      priceWrapper.appendChild(document.createTextNode(' '));
    }
    
    // Add current price
    const priceSpan = document.createElement('span');
    priceSpan.className = 'price';
    priceSpan.textContent = this.formatMoney(variant.price);
    priceWrapper.appendChild(priceSpan);
  }
  
  /**
   * Update compact price layout (product-card-price with detailed info)
   * Structure with discount: 
   * <div class="price-card-compact">
   *   <div class="price-card-compact__main">
   *     <div class="price price--on-sale">
   *       <span class="price__sale">11,52 €</span>
   *       <span class="price__compare">14,73 €</span>
   *     </div>
   *     <span class="price__badge-sale">-23%</span>
   *   </div>
   *   <div class="price-card-compact__savings">ahorra 3,21 €</div>
   * </div>
   * @param {HTMLElement} priceContainer - The product-card-price element
   * @param {Object} variant - Variant data
   */
  updateCompactPrice(priceContainer, variant) {
    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
    
    // Find the compact price wrapper
    const compactWrapper = priceContainer.querySelector('.price-card-compact');
    if (!compactWrapper) {
      return;
    }
    
    // Find the main section
    let mainSection = compactWrapper.querySelector('.price-card-compact__main');
    if (!mainSection) {
      // Create it if it doesn't exist
      mainSection = document.createElement('div');
      mainSection.className = 'price-card-compact__main';
      compactWrapper.appendChild(mainSection);
    }
    
    // Clear main section
    mainSection.innerHTML = '';
    
    // Create price div
    const priceDiv = document.createElement('div');
    priceDiv.className = hasDiscount ? 'price price--on-sale' : 'price';
    priceDiv.setAttribute('data-price', '');
    
    if (hasDiscount) {
      // Add sale price
      const saleSpan = document.createElement('span');
      saleSpan.className = 'price__sale';
      saleSpan.innerHTML = `<span class="visually-hidden">${this.texts.salePrice}</span>${this.formatMoney(variant.price)}`;
      priceDiv.appendChild(saleSpan);
      
      // Add compare price
      const compareSpan = document.createElement('span');
      compareSpan.className = 'price__compare';
      compareSpan.innerHTML = `<span class="visually-hidden">${this.texts.regularPrice}</span>${this.formatMoney(variant.compare_at_price)}`;
      priceDiv.appendChild(compareSpan);
      
      mainSection.appendChild(priceDiv);
      
      // Add discount badge
      const discountPercent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
      const badgeSpan = document.createElement('span');
      badgeSpan.className = 'price__badge-sale';
      badgeSpan.textContent = `-${discountPercent}%`;
      mainSection.appendChild(badgeSpan);
      
      // Add savings text
      const savingsAmount = variant.compare_at_price - variant.price;
      let savingsDiv = compactWrapper.querySelector('.price-card-compact__savings');
      if (!savingsDiv) {
        savingsDiv = document.createElement('div');
        savingsDiv.className = 'price-card-compact__savings';
        compactWrapper.appendChild(savingsDiv);
      }
      savingsDiv.textContent = `${this.texts.save} ${this.formatMoney(savingsAmount)}`;
    } else {
      // No discount - just regular price
      const regularSpan = document.createElement('span');
      regularSpan.className = 'price__regular';
      regularSpan.textContent = this.formatMoney(variant.price);
      priceDiv.appendChild(regularSpan);
      mainSection.appendChild(priceDiv);
      
      // Remove savings div if it exists
      const savingsDiv = compactWrapper.querySelector('.price-card-compact__savings');
      if (savingsDiv) {
        savingsDiv.remove();
      }
    }
  }
  
  /**
   * DEPRECATED: Old Section Rendering API method
   */
  async fetchRenderedProductCardsOld(productHandles) {
    console.group('🌐 Fetching rendered HTML via Section Rendering API');
    console.log('📊 Handles:', productHandles);
    
    const sectionId = this.recentlyViewedContainer.dataset.sectionId;
    console.log('🎯 Section ID:', sectionId);
    
    try {
      // Build URL with Section Rendering API format
      // Format: /?sections=section_id&product_handles[]=handle1&product_handles[]=handle2
      const params = new URLSearchParams();
      params.append('sections', sectionId);
      
      // Add each handle as a separate parameter
      productHandles.forEach(handle => {
        params.append('product_handles[]', handle);
      });
      
      const url = `/?${params.toString()}`;
      console.log('🔗 Fetch URL:', url);
      
      const response = await fetch(url);
      console.log('📦 Response status:', response.status);
      
      if (!response.ok) {
        console.error('❌ Response not OK');
        console.groupEnd();
        return null;
      }
      
      // Section Rendering API returns JSON with section HTML
      const data = await response.json();
      console.log('📦 Response data keys:', Object.keys(data));
      
      const sectionHTML = data[sectionId];
      
      if (!sectionHTML) {
        console.error('❌ Section HTML not found in response');
        console.groupEnd();
        return null;
      }
      
      console.log('✅ Section HTML received, length:', sectionHTML.length);
      
      // Extract only the recently viewed container content
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const container = doc.querySelector('[data-recently-viewed]');
      
      if (container) {
        const extractedHTML = container.innerHTML;
        console.log('✅ Extracted container HTML, length:', extractedHTML.length);
        
        // Debug: Check if products are in the HTML
        const hasProducts = extractedHTML.includes('product-card');
        const hasSkeletons = extractedHTML.includes('skeleton');
        const hasDebugComments = extractedHTML.includes('DEBUG:');
        console.log('🔍 HTML contains products:', hasProducts);
        console.log('🔍 HTML contains skeletons:', hasSkeletons);
        console.log('🔍 HTML contains DEBUG comments:', hasDebugComments);
        
        // Extract DEBUG comments if present
        if (hasDebugComments) {
          const debugMatch = extractedHTML.match(/<!-- DEBUG:.*?-->/g);
          if (debugMatch) {
            console.log('🐛 DEBUG comments found:');
            debugMatch.forEach(comment => console.log('  ', comment));
          }
        }
        
        console.log('📄 First 500 chars of HTML:', extractedHTML.substring(0, 500));
        
        console.groupEnd();
        return extractedHTML;
      } else {
        console.warn('⚠️ Container not found, returning full section HTML');
        console.groupEnd();
        return sectionHTML;
      }
    } catch (error) {
      console.error('❌ Fetch error:', error);
      console.groupEnd();
      return null;
    }
  }
  
  /**
   * Format money using Shopify's format
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   */
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '{{amount_with_comma_separator}} €');
    }
    
    // Fallback formatting - Shopify prices come in cents
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return `${euros} €`;
  }
  
  /**
   * DEPRECATED: Old fetch method - kept for reference
   */
  async fetchProductsOldVersion(productHandles) {
    console.group('🌐 DEBUG: Fetching Products');
    console.log('📊 Total products to fetch:', productHandles.length);
    console.log('🔗 Product handles:', productHandles);
    console.log('✅ Usando handles - Compatible con Shopify CLI local');
    
    // Fetch products in parallel usando handles
    const promises = productHandles.map((handle, index) => {
      const url = `/products/${handle}.js`;
      console.log(`  ${index + 1}. Fetching: ${url}`);
      
      return fetch(url)
        .then(response => {
          console.log(`  Response ${index + 1}:`, {
            url,
            status: response.status,
            ok: response.ok,
            statusText: response.statusText
          });
          return response.ok ? response.json() : null;
        })
        .then(data => {
          if (data) {
            console.log(`  ✅ Product ${index + 1} loaded:`, {
              id: data.id,
              handle: data.handle,
              title: data.title
            });
          } else {
            console.warn(`  ⚠️ Product ${index + 1} (${handle}) failed to load`);
          }
          return data;
        })
        .catch(error => {
          console.error(`  ❌ Error fetching product ${index + 1} (${handle}):`, error);
          return null;
        });
    });
    
    const results = await Promise.all(promises);
    
    const successfulProducts = results.filter(product => product !== null);
    console.log('📊 Fetch Summary:', {
      total: results.length,
      successful: successfulProducts.length,
      failed: results.length - successfulProducts.length
    });
    
    if (successfulProducts.length > 0) {
      console.table(successfulProducts.map(p => ({
        id: p.id,
        handle: p.handle,
        title: p.title,
        price: p.variants[0]?.price
      })));
    }
    
    console.groupEnd();
    
    // Filter out null results (products that don't exist or failed to load)
    return successfulProducts;
  } 
  
  /**
   * Render products in the container
   * @param {Array} products - Array of product objects
   */
  renderProducts(products) {
    console.log('🎨 DEBUG: Rendering products...');
    console.log('📦 Products to render:', products.length);
    
    const layoutType = this.recentlyViewedContainer.dataset.layoutType;
    const sectionId = this.recentlyViewedContainer.dataset.sectionId;
    
    console.log('📐 Layout type:', layoutType);
    console.log('🆔 Section ID:', sectionId);
    
    // Get settings from parent section - with null check
    const parentSection = this.closest('.section-resource-list');
    console.log('🔍 Parent section found:', parentSection ? 'Yes' : 'No');
    
    let columns = '4';
    let columnsGap = '16px';
    let rowsGap = '16px';
    
    if (parentSection) {
      columns = getComputedStyle(parentSection).getPropertyValue('--column-count') || '4';
      columnsGap = getComputedStyle(parentSection).getPropertyValue('--resource-list-column-gap-desktop') || '16px';
      rowsGap = getComputedStyle(parentSection).getPropertyValue('--resource-list-row-gap-desktop') || '16px';
      console.log('✅ Grid settings from parent:', { columns, columnsGap, rowsGap });
    } else {
      console.log('⚠️ Using default grid settings:', { columns, columnsGap, rowsGap });
    }
    
    // Build HTML
    let html = '';
    
    if (layoutType === 'grid') {
      console.log('📊 Rendering as GRID layout');
      html = '<div class="resource-list resource-list--grid">';
      products.forEach((product, index) => {
        console.log(`  Rendering product ${index + 1}/${products.length}: ${product.title}`);
        html += this.renderProductCard(product);
      });
      html += '</div>';
    } else if (layoutType === 'carousel') {
      console.log('🎠 Rendering as CAROUSEL layout');
      // For carousel, we need to use the existing carousel structure
      html = '<div class="resource-list resource-list__carousel force-full-width">';
      products.forEach((product, index) => {
        console.log(`  Rendering product ${index + 1}/${products.length}: ${product.title}`);
        html += this.renderProductCard(product);
      });
      html += '</div>';
    }
    
    console.log('📝 Generated HTML length:', html.length, 'characters');
    console.log('🔄 Injecting HTML into container...');
    this.recentlyViewedContainer.innerHTML = html;
    console.log('✅ Render complete!');
  }
  
  /**
   * Render a single product card
   * @param {Object} product - Product object from Shopify API
   * @returns {string} HTML string
   */
  renderProductCard(product) {
    const variant = product.variants[0];
    const image = product.featured_image || (product.images && product.images[0]);
    const price = this.formatMoney(variant.price);
    const compareAtPrice = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : null;
    
    return `
      <div class="resource-list__item">
        <product-card class="product-card" data-product-id="${product.id}">
          <a href="${product.url}" class="product-card__link">
            <span class="visually-hidden">${product.title}</span>
          </a>
          <div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card">
            ${image ? `
              <div class="product-card__media">
                <img 
                  src="${image}" 
                  alt="${product.title}"
                  loading="lazy"
                  width="300"
                  height="400"
                  style="aspect-ratio: 3/4; object-fit: cover;"
                >
              </div>
            ` : ''}
            <div class="product-card__info">
              <h3 class="product-card__title">
                <a href="${product.url}">${product.title}</a>
              </h3>
              <div class="product-card__price">
                ${compareAtPrice ? `
                  <span class="price price--on-sale">
                    <span class="price__sale">${price}</span>
                    <span class="price__compare">${compareAtPrice}</span>
                  </span>
                ` : `
                  <span class="price">${price}</span>
                `}
              </div>
            </div>
          </div>
        </product-card>
      </div>
    `;
  }
  
  /**
   * Show empty state when no products
   */
  showEmptyState() {
    this.recentlyViewedContainer.innerHTML = `
      <div class="recently-viewed-empty" data-no-products="true">
        <p>${this.texts.noRecentlyViewed}</p>
      </div>
    `;
    this.recentlyViewedLoaded = true;
  }
}

if (!customElements.get('product-tabs-component')) {
  customElements.define('product-tabs-component', ProductTabsComponent);
  console.log('✅ product-tabs-component registered');
} else {
  console.log('⚠️ product-tabs-component already registered');
}
